import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AsistenteIA } from './AsistenteIA';
import {
    LayoutDashboard, Users, Scissors, Factory, Droplets, Package,
    Archive, Truck, LogOut, Menu, X, ChevronDown, Layers,
    Shirt, Settings, Clock, FileText, DollarSign, ShoppingBag, ClipboardList, Store,
    Shield, Activity, Tag, ShoppingCart, CircleDot, PackageCheck, Paintbrush, Ribbon, Calculator
} from 'lucide-react';

const navGroups = [
    {
        label: 'General',
        items: [
            { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', slug: 'dashboard' },
        ]
    },
    {
        label: 'Produccion',
        items: [
            { path: '/lotes', icon: Layers, label: 'Lotes', slug: 'lotes' },
            { path: '/cortes', icon: Scissors, label: 'Cortes', slug: 'cortes' },
            { path: '/maquila', icon: Factory, label: 'Maquila', slug: 'maquila' },
            { path: '/lavanderia', icon: Droplets, label: 'Lavanderia', slug: 'lavanderia' },
            { path: '/habilitacion', icon: Package, label: 'Habilitacion', slug: 'habilitacion' },
        ]
    },
    {
        label: 'Inventarios',
        items: [
            { path: '/telas', icon: Shirt, label: 'Telas', slug: 'telas' },
            { path: '/cierres', icon: Archive, label: 'Cierres', slug: 'cierres' },
            { path: '/playeras', icon: Tag, label: 'Playeras', slug: 'playeras' },
            { path: '/producto-terminado', icon: PackageCheck, label: 'Prod. Terminado', slug: 'producto_terminado' },
            { path: '/botones', icon: CircleDot, label: 'Botones', slug: 'botones' },
            { path: '/bolsas', icon: ShoppingBag, label: 'Bolsas', slug: 'bolsas' },
            { path: '/cintas', icon: Ribbon, label: 'Cintas', slug: 'cintas' },
        ]
    },
    {
        label: 'Ventas',
        items: [
            { path: '/punto-venta', icon: ShoppingCart, label: 'Punto de Venta', slug: 'ventas' },
            { path: '/clientes', icon: Users, label: 'Clientes', slug: 'clientes' },
        ]
    },
    {
        label: 'Finanzas',
        items: [
            { path: '/proveedores', icon: Truck, label: 'Proveedores', slug: 'proveedores' },
            { path: '/cuentas-por-pagar', icon: DollarSign, label: 'Cuentas x Pagar', slug: 'cuentas_por_pagar' },
        ]
    },
    {
        label: 'Personal',
        items: [
            { path: '/empleados', icon: Users, label: 'Empleados', slug: 'empleados' },
            { path: '/asistencia', icon: Clock, label: 'Asistencia', slug: 'asistencia' },
            { path: '/nomina', icon: Calculator, label: 'Nomina / Reloj', slug: 'nomina' },
        ]
    },
    {
        label: 'Tienda',
        items: [
            { path: '/admin/productos', icon: ShoppingBag, label: 'Productos', slug: 'productos' },
            { path: '/admin/pedidos', icon: ClipboardList, label: 'Pedidos', slug: 'pedidos' },
            { path: '/apariencia', icon: Paintbrush, label: 'Apariencia', slug: 'productos' },
        ]
    },
    {
        label: 'Sistema',
        items: [
            { path: '/catalogos', icon: Settings, label: 'Catalogos', slug: 'catalogos' },
            { path: '/reportes', icon: FileText, label: 'Reportes', slug: 'reportes' },
        ]
    }
];

export const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user, logout, hasPermission, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const NavContent = () => (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-5 border-b border-white/10">
                <Link to="/dashboard" className="flex items-center gap-3">
                    <img src="/logo-karry.png" alt="KJ" className="w-9 h-9 rounded-lg object-contain" />
                    {sidebarOpen && (
                        <div>
                            <h1 className="font-display text-lg text-white tracking-wider leading-none">KARRY JEAN'S</h1>
                            <p className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase mt-0.5">Production System</p>
                        </div>
                    )}
                </Link>
            </div>

            {/* Store Link */}
            <div className="px-3 pt-3 space-y-1">
                <a href="/tienda" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors text-sm">
                    <Store className="w-4 h-4" />
                    {sidebarOpen && <span>Ver Tienda</span>}
                </a>
                {hasPermission('productos') && (
                    <Link to="/apariencia" onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            location.pathname === '/apariencia'
                                ? 'bg-amber-400 text-zinc-900'
                                : 'text-zinc-400 hover:text-amber-400 hover:bg-white/5'
                        }`}>
                        <Paintbrush className="w-4 h-4" />
                        {sidebarOpen && <span>Apariencia</span>}
                    </Link>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                {navGroups.map((group) => {
                    const visibleItems = group.items.filter(item => hasPermission(item.slug));
                    if (visibleItems.length === 0) return null;
                    return (
                        <div key={group.label}>
                            {sidebarOpen && (
                                <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500">
                                    {group.label}
                                </p>
                            )}
                            <div className="space-y-0.5">
                                {visibleItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = location.pathname === item.path;
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setMobileOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                active
                                                    ? 'bg-amber-400 text-zinc-900'
                                                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                                            }`}
                                        >
                                            <Icon className="w-[18px] h-[18px] shrink-0" />
                                            {sidebarOpen && <span>{item.label}</span>}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {/* Admin-only section */}
                {isAdmin && (
                    <div>
                        {sidebarOpen && (
                            <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500">
                                Administración
                            </p>
                        )}
                        <div className="space-y-0.5">
                            <Link to="/usuarios" onClick={() => setMobileOpen(false)}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    location.pathname === '/usuarios' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                                }`}>
                                <Shield className="w-[18px] h-[18px] shrink-0" />
                                {sidebarOpen && <span>Usuarios</span>}
                            </Link>
                            <Link to="/actividad" onClick={() => setMobileOpen(false)}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    location.pathname === '/actividad' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                                }`}>
                                <Activity className="w-[18px] h-[18px] shrink-0" />
                                {sidebarOpen && <span>Actividad</span>}
                            </Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* User & Logout */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-zinc-900">
                            {user?.username?.[0]?.toUpperCase() || 'A'}
                        </span>
                    </div>
                    {sidebarOpen && (
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{user?.nombre || user?.username}</p>
                            <p className="text-[10px] text-zinc-500 capitalize">{user?.rol}</p>
                        </div>
                    )}
                    <button onClick={handleLogout} className="p-1.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-white/5 transition-colors" data-testid="logout-btn">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0C0C0E]">
            {/* Desktop Sidebar */}
            <aside className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen bg-zinc-900 border-r border-white/5 z-40 transition-all duration-300 ${sidebarOpen ? 'w-60' : 'w-[68px]'}`}>
                <NavContent />
            </aside>

            {/* Mobile overlay */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                            onClick={() => setMobileOpen(false)}
                        />
                        <motion.aside
                            initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed top-0 left-0 w-64 h-screen bg-zinc-900 z-50 lg:hidden"
                        >
                            <NavContent />
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            {/* Main */}
            <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-60' : 'lg:ml-[68px]'}`}>
                {/* Topbar */}
                <header className="sticky top-0 z-30 bg-[#111113]/80 backdrop-blur-xl border-b border-zinc-800/50">
                    <div className="flex items-center justify-between h-14 px-4">
                        <div className="flex items-center gap-3">
                            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-lg hover:bg-zinc-800 text-zinc-400">
                                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="hidden lg:flex p-2 rounded-lg hover:bg-zinc-800 text-zinc-500"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="p-4 lg:p-6">
                    {children}
                </main>
            </div>
            <AsistenteIA />
        </div>
    );
};
